import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiTrash2, FiSearch, FiCalendar, FiUser, FiMail, FiBriefcase, FiMapPin, FiPhone, FiX } = FiIcons;

const SearchHistory = ({ onHistorySelect, isLoading }) => {
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);

  // Load search history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('osintSearchHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        setSearchHistory(parsed);
      } catch (error) {
        console.error('Error parsing search history:', error);
      }
    }
  }, []);

  // Save search history to localStorage whenever it changes
  useEffect(() => {
    if (searchHistory.length > 0) {
      localStorage.setItem('osintSearchHistory', JSON.stringify(searchHistory));
    }
  }, [searchHistory]);

  // Add new search to history
  const addSearchToHistory = (searchData, results) => {
    const historyItem = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      searchData: { ...searchData },
      results: {
        score: results.score,
        assessment: results.assessment,
        imagesCount: results.images?.length || 0,
        redditPostsCount: results.redditPosts?.length || 0,
        socialMediaCount: Object.keys(results.socialMedia || {}).length,
        storiesCount: results.stories?.length || 0,
        redFlagsCount: results.redFlags?.length || 0
      }
    };

    // Add to beginning of array and keep only last 20 searches
    setSearchHistory(prev => [historyItem, ...prev.slice(0, 19)]);
  };

  // Expose addSearchToHistory to parent component
  React.useImperativeHandle(React.useRef(), () => ({
    addSearchToHistory
  }));

  // Delete single search from history
  const deleteSearch = (id) => {
    setSearchHistory(prev => prev.filter(item => item.id !== id));
  };

  // Clear all search history
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('osintSearchHistory');
    setShowHistory(false);
  };

  // Re-run search from history
  const rerunSearch = (historyItem) => {
    if (!isLoading) {
      onHistorySelect(historyItem.searchData);
      setShowHistory(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 70) return 'text-blue-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (searchHistory.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Toggle History Button */}
      <motion.button
        onClick={() => setShowHistory(!showHistory)}
        className="flex items-center space-x-2 text-purple-300 hover:text-white transition-colors mb-4"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <SafeIcon icon={FiClock} className="text-lg" />
        <span className="text-sm">
          Search History ({searchHistory.length})
        </span>
        <motion.div
          animate={{ rotate: showHistory ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <SafeIcon icon={FiSearch} className="text-sm" />
        </motion.div>
      </motion.button>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-black/30 backdrop-blur-lg rounded-2xl p-6 border border-purple-500/20">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <SafeIcon icon={FiClock} className="text-purple-400" />
                  <span>Recent Searches</span>
                </h3>
                <button
                  onClick={clearHistory}
                  className="flex items-center space-x-1 text-red-400 hover:text-red-300 text-sm transition-colors"
                >
                  <SafeIcon icon={FiTrash2} />
                  <span>Clear All</span>
                </button>
              </div>

              {/* History Items */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {searchHistory.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white/5 rounded-lg p-4 border border-purple-500/20 hover:border-purple-400/40 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Search Details */}
                        <div className="flex items-center space-x-2 mb-2">
                          <SafeIcon icon={FiUser} className="text-xs text-purple-400" />
                          <span className="text-white font-medium">{item.searchData.name}</span>
                          <span className={`text-sm font-medium ${getScoreColor(item.results.score)}`}>
                            ({item.results.score})
                          </span>
                        </div>

                        {/* Additional Search Info */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {item.searchData.email && (
                            <div className="flex items-center space-x-1 text-xs text-purple-300">
                              <SafeIcon icon={FiMail} />
                              <span>{item.searchData.email}</span>
                            </div>
                          )}
                          {item.searchData.company && (
                            <div className="flex items-center space-x-1 text-xs text-purple-300">
                              <SafeIcon icon={FiBriefcase} />
                              <span>{item.searchData.company}</span>
                            </div>
                          )}
                          {item.searchData.location && (
                            <div className="flex items-center space-x-1 text-xs text-purple-300">
                              <SafeIcon icon={FiMapPin} />
                              <span>{item.searchData.location}</span>
                            </div>
                          )}
                        </div>

                        {/* Results Summary */}
                        <div className="flex items-center space-x-3 text-xs text-purple-400 mb-2">
                          <span>{item.results.imagesCount} images</span>
                          <span>{item.results.socialMediaCount} social</span>
                          <span>{item.results.redditPostsCount} reddit</span>
                          <span>{item.results.storiesCount} web</span>
                          {item.results.redFlagsCount > 0 && (
                            <span className="text-red-400">{item.results.redFlagsCount} flags</span>
                          )}
                        </div>

                        {/* Timestamp */}
                        <div className="flex items-center space-x-1 text-xs text-purple-500">
                          <SafeIcon icon={FiCalendar} />
                          <span>{formatDate(item.timestamp)}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => rerunSearch(item)}
                          disabled={isLoading}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Re-run search"
                        >
                          <SafeIcon icon={FiSearch} className="text-sm" />
                        </button>
                        <button
                          onClick={() => deleteSearch(item.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all duration-300"
                          title="Delete from history"
                        >
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-purple-500/20">
                <p className="text-xs text-purple-400 text-center">
                  Search history is stored locally in your browser. Clear history to remove all stored searches.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchHistory;